import { z } from "zod";

import type { ResponseStreamChunk } from "../responses/responses.models";
import { collectLeafZodIssues } from "../../zodIssues";

/** ******************************************************************************
 *  Chat Completion Request Message Content Parts
 *
 *  The request schemas mirror the OpenAI SDK chat message contracts by role.
 *  User content parts stay forward-compatible because providers add input
 *  modalities faster than this package can release; known content types still
 *  get strict validation.
 *
 *  INF-5359 divergence: tool-result messages (and assistant echoes in agent
 *  histories) also accept image_url / input_audio / file parts. Agentic callers
 *  feed images back through tool results to vision-capable models; the OpenAI
 *  SDK param contracts are text-only for these roles, but rejecting the part
 *  at the gateway 400s valid vision-model traffic (kimi-k3). system/developer
 *  stay text-only; tool keeps strict validation for unknown part types.
 ******************************************************************************* */

export const ChatCompletionRequestMessageContentPartTextSchema = z
  .object({
    type: z.literal("text"),
    text: z.string(),
  })
  .passthrough();

export type ChatCompletionRequestMessageContentPartText = z.infer<
  typeof ChatCompletionRequestMessageContentPartTextSchema
>;

const base64ImageDataURL =
  /^data:image\/(png|jpeg|jpg|gif|webp);(charset=[^;]+,)?base64,([A-Za-z0-9+/]+={0,2})$/i;

const httpUrlPattern = /^https?:\/\//i;

export const ChatCompletionRequestMessageContentPartImageSchema = z
  .object({
    type: z.literal("image_url"),
    image_url: z
      .object({
        url: z
          .string()
          .url()
          .refine(
            (url) => httpUrlPattern.test(url) || base64ImageDataURL.test(url),
            "Invalid image URL - must be either an HTTP/HTTPS URL or a base64-encoded data URL with mimetypes image/png, image/jpeg, image/jpg, image/gif, or image/webp",
          ),
      })
      .passthrough(),
  })
  .passthrough();

export type ChatCompletionRequestMessageContentPartImage = z.infer<
  typeof ChatCompletionRequestMessageContentPartImageSchema
>;

export const ChatCompletionRequestMessageContentPartRefusalSchema = z
  .object({
    type: z.literal("refusal"),
    refusal: z.string(),
  })
  .passthrough();

export type ChatCompletionRequestMessageContentPartRefusal = z.infer<
  typeof ChatCompletionRequestMessageContentPartRefusalSchema
>;

export const ChatCompletionRequestMessageContentPartInputAudioSchema = z
  .object({
    type: z.literal("input_audio"),
    input_audio: z
      .object({
        data: z.string(),
        format: z.enum(["wav", "mp3"]),
      })
      .passthrough(),
  })
  .passthrough();

export type ChatCompletionRequestMessageContentPartInputAudio = z.infer<
  typeof ChatCompletionRequestMessageContentPartInputAudioSchema
>;

export const ChatCompletionRequestMessageContentPartFileSchema = z
  .object({
    type: z.literal("file"),
    file: z
      .object({
        file_data: z.string().optional(),
        file_id: z.string().optional(),
        filename: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough();

export type ChatCompletionRequestMessageContentPartFile = z.infer<
  typeof ChatCompletionRequestMessageContentPartFileSchema
>;

export const ChatCompletionRequestUnknownContentPartSchema = z
  .object({ type: z.string() })
  .passthrough();

export type ChatCompletionRequestUnknownContentPart = z.infer<
  typeof ChatCompletionRequestUnknownContentPartSchema
>;

function createContentPartSchema<T>(
  schemasByType: Record<string, z.ZodTypeAny>,
  unknownSchema?: z.ZodTypeAny,
): z.ZodType<T> {
  return z.unknown().superRefine((part, ctx) => {
    const typeResult = z
      .object({ type: z.string() })
      .passthrough()
      .safeParse(part);
    if (!typeResult.success) {
      for (const issue of collectLeafZodIssues(typeResult.error)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: issue.path,
          message: issue.message,
        });
      }
      return;
    }

    const schema = schemasByType[typeResult.data.type] ?? unknownSchema;
    if (!schema) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["type"],
        message: `Unsupported content part type '${typeResult.data.type}'.`,
      });
      return;
    }

    const result = schema.safeParse(part);
    if (result.success) {
      return;
    }

    for (const issue of collectLeafZodIssues(result.error)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: issue.path,
        message: issue.message,
      });
    }
  }) as z.ZodType<T>;
}

const textContentPartSchemasByType = {
  text: ChatCompletionRequestMessageContentPartTextSchema,
};

const userContentPartSchemasByType = {
  ...textContentPartSchemasByType,
  image_url: ChatCompletionRequestMessageContentPartImageSchema,
  input_audio: ChatCompletionRequestMessageContentPartInputAudioSchema,
  file: ChatCompletionRequestMessageContentPartFileSchema,
};

const toolContentPartSchemasByType = userContentPartSchemasByType;

const assistantContentPartSchemasByType = {
  ...userContentPartSchemasByType,
  refusal: ChatCompletionRequestMessageContentPartRefusalSchema,
};

export type ChatCompletionRequestTextContentPart =
  ChatCompletionRequestMessageContentPartText;

export type ChatCompletionRequestUserContentPart =
  | ChatCompletionRequestMessageContentPartText
  | ChatCompletionRequestMessageContentPartImage
  | ChatCompletionRequestMessageContentPartInputAudio
  | ChatCompletionRequestMessageContentPartFile
  | ChatCompletionRequestUnknownContentPart;

export type ChatCompletionRequestToolContentPart =
  ChatCompletionRequestUserContentPart;

export type ChatCompletionRequestAssistantContentPart =
  | ChatCompletionRequestToolContentPart
  | ChatCompletionRequestMessageContentPartRefusal;

export type ChatCompletionRequestMessageContentPart =
  | ChatCompletionRequestAssistantContentPart
  | ChatCompletionRequestUserContentPart;

export const ChatCompletionRequestTextContentPartSchema =
  createContentPartSchema<ChatCompletionRequestTextContentPart>(
    textContentPartSchemasByType,
  );

export const ChatCompletionRequestAssistantContentPartSchema =
  createContentPartSchema<ChatCompletionRequestAssistantContentPart>(
    assistantContentPartSchemasByType,
  );

export const ChatCompletionRequestUserContentPartSchema =
  createContentPartSchema<ChatCompletionRequestUserContentPart>(
    userContentPartSchemasByType,
    ChatCompletionRequestUnknownContentPartSchema,
  );

export const ChatCompletionRequestToolContentPartSchema =
  createContentPartSchema<ChatCompletionRequestToolContentPart>(
    toolContentPartSchemasByType,
    ChatCompletionRequestUnknownContentPartSchema,
  );

export const ChatCompletionRequestMessageContentPartSchema =
  createContentPartSchema<ChatCompletionRequestMessageContentPart>(
    {
      ...assistantContentPartSchemasByType,
      image_url: ChatCompletionRequestMessageContentPartImageSchema,
      input_audio: ChatCompletionRequestMessageContentPartInputAudioSchema,
      file: ChatCompletionRequestMessageContentPartFileSchema,
    },
    ChatCompletionRequestUnknownContentPartSchema,
  );

const textMessageContentSchema = z.union([
  z.string(),
  z.array(ChatCompletionRequestTextContentPartSchema),
]);

const assistantMessageContentSchema = z.union([
  z.string(),
  z.array(ChatCompletionRequestAssistantContentPartSchema),
]);

const userMessageContentSchema = z.union([
  z.string(),
  z.array(ChatCompletionRequestUserContentPartSchema),
]);

const toolMessageContentSchema = z.union([
  z.string(),
  z.array(ChatCompletionRequestToolContentPartSchema),
]);

export const ChatCompletionRequestSystemMessageSchema = z
  .object({
    role: z.literal("system"),
    content: textMessageContentSchema,
    name: z.string().optional(),
  })
  .passthrough();

export type ChatCompletionRequestSystemMessage = z.infer<
  typeof ChatCompletionRequestSystemMessageSchema
>;

export const ChatCompletionRequestUserMessageSchema = z
  .object({
    role: z.literal("user"),
    content: userMessageContentSchema,
    name: z.string().optional(),
  })
  .passthrough();

export type ChatCompletionRequestUserMessage = z.infer<
  typeof ChatCompletionRequestUserMessageSchema
>;

export const ChatCompletionRequestDeveloperMessageSchema = z
  .object({
    role: z.literal("developer"),
    content: textMessageContentSchema,
    name: z.string().optional(),
  })
  .passthrough();

export type ChatCompletionRequestDeveloperMessage = z.infer<
  typeof ChatCompletionRequestDeveloperMessageSchema
>;

/** ******************************************************************************
 *  Chat Completion Message Tool Call
 ******************************************************************************* */

export const ChatCompletionMessageFunctionToolCallSchema = z
  .object({
    id: z.string(),
    type: z.literal("function"),
    function: z
      .object({ name: z.string(), arguments: z.string() })
      .passthrough(),
    custom: z.undefined().optional(),
  })
  .passthrough();

export type ChatCompletionMessageFunctionToolCall = z.infer<
  typeof ChatCompletionMessageFunctionToolCallSchema
>;

export const ChatCompletionMessageCustomToolCallSchema = z
  .object({
    id: z.string(),
    type: z.literal("custom"),
    custom: z.object({ name: z.string(), input: z.string() }).passthrough(),
    function: z.undefined().optional(),
  })
  .passthrough();

export type ChatCompletionMessageCustomToolCall = z.infer<
  typeof ChatCompletionMessageCustomToolCallSchema
>;

export const ChatCompletionMessageToolCallSchema = z.union([
  ChatCompletionMessageFunctionToolCallSchema,
  ChatCompletionMessageCustomToolCallSchema,
]);

export type ChatCompletionMessageToolCall = z.infer<
  typeof ChatCompletionMessageToolCallSchema
>;

/** ******************************************************************************
 *  Chat Completion Message Tool Calls
 ******************************************************************************* */

export const ChatCompletionMessageToolCallsSchema = z.array(
  ChatCompletionMessageToolCallSchema,
);

export type ChatCompletionMessageToolCalls = z.infer<
  typeof ChatCompletionMessageToolCallsSchema
>;

export const ChatCompletionRequestAssistantMessageSchema = z
  .object({
    role: z.literal("assistant"),
    content: assistantMessageContentSchema.nullish(),
    reasoning_content: z.string().nullish(),
    name: z.string().optional(),
    tool_calls: ChatCompletionMessageToolCallsSchema.optional(),
    function_call: z
      .object({ arguments: z.string(), name: z.string() })
      .passthrough()
      .optional(),
  })
  .passthrough();

export type ChatCompletionRequestAssistantMessage = z.infer<
  typeof ChatCompletionRequestAssistantMessageSchema
>;

export const ChatCompletionRequestToolMessageSchema = z
  .object({
    role: z.literal("tool"),
    content: toolMessageContentSchema,
    tool_call_id: z.string(),
  })
  .passthrough();

export type ChatCompletionRequestToolMessage = z.infer<
  typeof ChatCompletionRequestToolMessageSchema
>;

export const ChatCompletionRequestFunctionMessageSchema = z
  .object({
    role: z.literal("function"),
    content: z.string().nullable(),
    name: z.string(),
  })
  .passthrough();

export type ChatCompletionRequestFunctionMessage = z.infer<
  typeof ChatCompletionRequestFunctionMessageSchema
>;

const knownMessageSchemaByRole: Record<string, z.ZodTypeAny> = {
  system: ChatCompletionRequestSystemMessageSchema,
  developer: ChatCompletionRequestDeveloperMessageSchema,
  user: ChatCompletionRequestUserMessageSchema,
  assistant: ChatCompletionRequestAssistantMessageSchema,
  tool: ChatCompletionRequestToolMessageSchema,
  function: ChatCompletionRequestFunctionMessageSchema,
};

export const CHAT_COMPLETION_REQUEST_MESSAGE_ROLES = Object.keys(
  knownMessageSchemaByRole,
);

export type ChatCompletionRequestUnknownMessage = {
  role: string;
  [key: string]: unknown;
};

export type ChatCompletionRequestMessage =
  | ChatCompletionRequestSystemMessage
  | ChatCompletionRequestDeveloperMessage
  | ChatCompletionRequestUserMessage
  | ChatCompletionRequestAssistantMessage
  | ChatCompletionRequestToolMessage
  | ChatCompletionRequestFunctionMessage
  | ChatCompletionRequestUnknownMessage;

export const ChatCompletionRequestMessageSchema: z.ZodType<ChatCompletionRequestMessage> =
  z
    .object({ role: z.string() })
    .passthrough()
    .superRefine((message, ctx) => {
      const roleSchema = Object.hasOwn(knownMessageSchemaByRole, message.role)
        ? knownMessageSchemaByRole[message.role]
        : undefined;

      if (!roleSchema) {
        return;
      }

      const result = roleSchema.safeParse(message);
      if (result.success) {
        return;
      }

      for (const issue of collectLeafZodIssues(result.error)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: issue.path,
          message: issue.message,
        });
      }
    });

/** ******************************************************************************
 *  Function Parameters
 ******************************************************************************* */

// Per openai, function parameters can be almost any JSON schema specification: https://platform.openai.com/docs/guides/function-calling#defining-functions
// Writing a zod schema for this is impractical, so we accept any valid JSON schema structure.
export const FunctionParametersSchema = z.record(z.string(), z.any());

/** ******************************************************************************
 *  Function Object
 ******************************************************************************* */

export const FunctionObjectSchema = z
  .object({
    description: z.string().optional(),
    name: z.string(),
    parameters: FunctionParametersSchema.optional(),
    strict: z.boolean().optional(),
  })
  .passthrough();

/** ******************************************************************************
 *  Chat Completion Tool
 ******************************************************************************* */

export const ChatCompletionToolSchema = z
  .object({
    type: z.literal("function"),
    function: FunctionObjectSchema,
  })
  .passthrough();

export type ChatCompletionTool = z.infer<typeof ChatCompletionToolSchema>;

/** ******************************************************************************
 *  Chat Completion Named Tool Choice
 ******************************************************************************* */

export const ChatCompletionNamedToolChoiceSchema = z
  .object({
    type: z.literal("function"),
    function: z.object({ name: z.string() }).passthrough(),
  })
  .passthrough();

/** ******************************************************************************
 *  Chat Completion Tool Choice Option
 ******************************************************************************* */

export const ChatCompletionToolChoiceOptionSchema = z.union([
  z.enum(["none", "auto", "required"]),
  ChatCompletionNamedToolChoiceSchema,
]);

export type ChatCompletionToolChoiceOption = z.infer<
  typeof ChatCompletionToolChoiceOptionSchema
>;

/** ******************************************************************************
 *  Chat Completion Request
 ******************************************************************************* */

const ResponseFormatSchema = z.union([
  z
    .object({
      type: z.enum(["text", "json_object"]),
    })
    .passthrough(),
  z
    .object({
      type: z.literal("json_schema"),
      json_schema: z
        .object({
          name: z.string(),
          description: z.string().optional(),
          strict: z.boolean().optional(),
          schema: z.record(z.string(), z.any()).optional(),
        })
        .passthrough(),
    })
    .passthrough(),
]);

/** ******************************************************************************
 *  Completion Request Metadata
 ******************************************************************************* */

export const MetadataSchema = z
  .object({
    webhook_id: z.string().optional(),
  })
  .passthrough();

const baseCompletionSchema = z.object({
  model: z.string(),
  frequency_penalty: z.number().nullish(),
  instance_id: z.string().nullish(),
  logit_bias: z.record(z.string(), z.number()).nullish(),
  logprobs: z.boolean().nullish(),
  top_logprobs: z.number().nullish(),
  max_tokens: z.number().nullish(),
  max_completion_tokens: z.number().nullish(),
  n: z.number().nullish(),
  presence_penalty: z.number().nullish(),
  response_format: ResponseFormatSchema.nullish(),
  seed: z.number().nullish(),
  stop: z.union([z.string(), z.array(z.string())]).nullish(),
  stream: z.boolean().nullish(),
  temperature: z.number().nullish(),
  top_p: z.number().nullish(),
  user: z.string().nullish(),
  function_call: z.any().nullish(),
  functions: z.array(z.any()).nullish(),
  top_k: z.number().nullish(),
  min_p: z.number().nullish(),
  metadata: z.record(z.string(), z.any()).nullish(),
  parallel_tool_calls: z.boolean().nullish(),
  prompt_cache_key: z.unknown().nullish(),
  prompt_cache_retention: z.unknown().nullish(),
  reasoning_effort: z.string().nullish(),
});

type BaseCompletionRequest = z.infer<typeof baseCompletionSchema>;

type ChatCompletionStreamOptions = {
  include_usage?: boolean | null;
};

const baseCompletionPassthroughSchema = baseCompletionSchema.passthrough();

export type V1ChatCompletionRequest = BaseCompletionRequest & {
  messages: ChatCompletionRequestMessage[];
  tools?: ChatCompletionTool[] | null;
  tool_choice?: ChatCompletionToolChoiceOption | null;
  stream_options?: ChatCompletionStreamOptions | null;
};

// per (roughly) this: https://platform.openai.com/docs/api-reference/chat/create
// but actually closer to this?: https://github.com/ollama/ollama/blob/581a4a55532e2a28177bcaffd722a2bf298f967d/openai/openai.go#L78
export const V1ChatCompletionRequestSchema =
  baseCompletionPassthroughSchema.extend({
    messages: z.array(ChatCompletionRequestMessageSchema).min(1),
    tools: z.array(ChatCompletionToolSchema).nullish(),
    tool_choice: ChatCompletionToolChoiceOptionSchema.nullish(),
    stream_options: z
      .object({ include_usage: z.boolean().nullish() })
      .passthrough()
      .nullish(),
  }) as z.ZodType<V1ChatCompletionRequest>;

// per (roughly) this: https://platform.openai.com/docs/api-reference/completions/create#completions/create
// but actually this: https://github.com/ollama/ollama/blob/581a4a55532e2a28177bcaffd722a2bf298f967d/openai/openai.go#L113
// (ollama doesn't support best_of and echo, for example)
export type V1TextCompletionRequest = BaseCompletionRequest & {
  prompt: string;
};

export const V1TextCompletionRequestSchema =
  baseCompletionPassthroughSchema.extend({
    prompt: z.string(),
    // Note: Although suffix is listed in the Ollana API docs *and* in the OpenAI API docs, it doesn't work in Ollama.
    //suffix: z.string().nullish(),
  }) as z.ZodType<V1TextCompletionRequest>;

/** ******************************************************************************
 *  Chat Completion Response Message
 ******************************************************************************* */

export const ChatCompletionResponseMessageSchema = z
  .object({
    // `content` is `nullish` rather than `nullable` because real-world
    // responses for audio-only or refusal-only completions may OMIT the key
    // entirely. Strict `nullable()` would require the key be present.
    content: z.string().nullish(),
    reasoning_content: z.string().nullish(),
    reasoning: z.string().nullish(),
    tool_calls: ChatCompletionMessageToolCallsSchema.optional().nullable(),
    role: z.literal("assistant"),
    function_call: z
      .object({ arguments: z.string(), name: z.string() })
      .nullish(),
    // OpenAI 2024+ — refusal text on safety-rejected completions.
    refusal: z.string().nullish(),
    // OpenAI 2024+ — audio response payload for gpt-4o-audio-preview etc.
    // Kept opaque because callers don't introspect it for billing/usage.
    audio: z.unknown().nullish(),
    // OpenAI 2025+ — web-search tool citations (and similar).
    annotations: z.array(z.unknown()).nullish(),
  })
  // Allow unknown future fields to round-trip without rejection.
  .passthrough();

export type ChatCompletionResponseMessage = z.infer<
  typeof ChatCompletionResponseMessageSchema
>;

/** ******************************************************************************
 *  Token Logprob
 ******************************************************************************* */

export const LogprobsContentSchema = z.object({
  token: z.string(),
  logprob: z.number(),
  bytes: z.array(z.number()).nullable(),
  top_logprobs: z.array(
    z.object({
      token: z.string(),
      logprob: z.number(),
      bytes: z.array(z.number()).nullable(),
    }),
  ),
});

export type LogprobsContent = z.infer<typeof LogprobsContentSchema>;

export const LogprobsSchema = z.object({
  content: z.array(LogprobsContentSchema),
});

export type Logprobs = z.infer<typeof LogprobsSchema>;

/** ******************************************************************************
 *  Token Logprob (Token IDs)
 ******************************************************************************* */

export const LogprobsWithTokenIdsContentSchema = z.object({
  token_id: z.number(),
  logprob: z.number(),
  top_logprobs: z.array(
    z.object({
      token_id: z.number(),
      logprob: z.number(),
    }),
  ),
});

export type LogprobsWithTokenIdsContent = z.infer<
  typeof LogprobsWithTokenIdsContentSchema
>;

export const LogprobsWithTokenIdsSchema = z.object({
  content: z.array(LogprobsWithTokenIdsContentSchema),
});

export type LogprobsWithTokenIds = z.infer<typeof LogprobsWithTokenIdsSchema>;

/** ******************************************************************************
 *  Completion Usage
 ******************************************************************************* */

export const CompletionUsageSchema = z.object({
  completion_tokens: z.number(),
  prompt_tokens: z.number(),
  total_tokens: z.number(),
  completion_tokens_details: z
    .object({
      reasoning_tokens: z.number().optional(),
      accepted_prediction_tokens: z.number().optional(),
      rejected_prediction_tokens: z.number().optional(),
    })
    .nullish(),
  // sglang (own fleet) reports reasoning at the top level instead of nesting
  // it under completion_tokens_details. It counts generated tokens through the
  // think-end marker and is included in completion_tokens; total_tokens stays
  // prompt + completion.
  reasoning_tokens: z.number().nullish(),
  prompt_tokens_details: z.any().nullish(),
});

export type CompletionUsage = z.infer<typeof CompletionUsageSchema>;

/** ******************************************************************************
 *  Create Chat Completion Response
 ******************************************************************************* */

export const FinishReasonSchema = z.union([
  z.enum([
    "stop",
    "length",
    "tool_calls",
    "content_filter",
    "function_call",
    "error",
  ]),
  z.string(),
]);

export const TextCompletionResponseChoiceSchema = z.object({
  text: z.string(),
  index: z.number().int(),
  finish_reason: FinishReasonSchema.nullable(),
  logprobs: LogprobsSchema.nullable(),
});

export type TextCompletionResponseChoice = z.infer<
  typeof TextCompletionResponseChoiceSchema
>;

export const ChatCompletionResponseChoiceSchema = z.object({
  finish_reason: FinishReasonSchema.nullable(),
  index: z.number().int(),
  message: ChatCompletionResponseMessageSchema,
  logprobs: LogprobsSchema.nullable().optional(),

  // Additional field that may be present in responses
  matched_stop: z.string().nullish(),
});

export type ChatCompletionResponseChoice = z.infer<
  typeof ChatCompletionResponseChoiceSchema
>;

const BaseCompletionResponseSchema = z.object({
  id: z.string(),
  created: z.number().int(),
  model: z.string(),
  system_fingerprint: z.string().nullable().optional(),
  usage: CompletionUsageSchema.optional(),

  metadata: MetadataSchema.optional(),
});

export const V1ChatCompletionResponseSchema =
  BaseCompletionResponseSchema.extend({
    choices: z.array(ChatCompletionResponseChoiceSchema),
    object: z.literal("chat.completion"),
  });

export type V1ChatCompletionResponse = z.infer<
  typeof V1ChatCompletionResponseSchema
>;

export const V1CompletionResponseSchema = BaseCompletionResponseSchema.extend({
  choices: z.array(TextCompletionResponseChoiceSchema),
  object: z.literal("text_completion"),
});

export type V1CompletionResponse = z.infer<typeof V1CompletionResponseSchema>;

/** ******************************************************************************
 *  Embedding Response
 ******************************************************************************* */

export const EmbeddingUsageSchema = z.object({
  prompt_tokens: z.number(),
  total_tokens: z.number(),
  completion_tokens: z.number().optional(),
});

export type EmbeddingUsage = z.infer<typeof EmbeddingUsageSchema>;

export const EmbeddingObjectSchema = z.object({
  object: z.literal("embedding"),
  embedding: z.array(z.number()),
  index: z.number().int(),
});

export type EmbeddingObject = z.infer<typeof EmbeddingObjectSchema>;

export const V1EmbeddingResponseSchema = z.object({
  object: z.literal("list"),
  data: z.array(EmbeddingObjectSchema),
  model: z.string(),
  usage: EmbeddingUsageSchema.optional(),
});

export type V1EmbeddingResponse = z.infer<typeof V1EmbeddingResponseSchema>;

/** ******************************************************************************
 *  Embedding Request
 ******************************************************************************* */

export const V1EmbeddingRequestSchema = z
  .object({
    model: z.string(),
    input: z.union([z.string(), z.array(z.string()), z.array(z.number())]),
    encoding_format: z.enum(["float", "base64"]).optional(),
    dimensions: z.number().optional(),
  })
  .passthrough();

export type V1EmbeddingRequest = z.infer<typeof V1EmbeddingRequestSchema>;

/** ******************************************************************************
 *  Create Chat Completion Chunk Response
 ******************************************************************************* */

export const ChatCompletionChunkFunctionCallSchema = z.object({
  arguments: z.string().optional(),
  name: z.string().optional(),
});

export type ChatCompletionChunkFunctionCall = z.infer<
  typeof ChatCompletionChunkFunctionCallSchema
>;

export const ChatCompletionChunkFunctionSchema = z.object({
  arguments: z.string().optional(),
  // Nullable in addition to optional: providers (e.g. glm-5.3-flash, kimi-k3)
  // send `"name": null` on tool-call continuation deltas. `.optional()` only
  // accepts `undefined`; `null` failed validation and dropped the whole chunk.
  name: z.string().nullish(),
});

export type ChatCompletionChunkFunction = z.infer<
  typeof ChatCompletionChunkFunctionSchema
>;

export const ChatCompletionChunkToolCallSchema = z.object({
  index: z.number().int(),
  // Nullable for the same reason as `name` above: providers send `"id": null`
  // on continuation deltas (the id is only emitted on the first tool-call
  // chunk of a stream). `.optional()` rejected `null` and failed the chunk.
  id: z.string().nullish(),
  function: ChatCompletionChunkFunctionSchema,
  // Streaming tool-call deltas are forwarded from upstream workers without
  // re-validation, so `type` reflects whatever the provider sent: it may be
  // absent (some fine-tuned providers omit it on continuation deltas, esp. the
  // 2nd+ call in a multi-tool stream) or, in principle, an unrecognized variant.
  // Model it as an optional string rather than a strict `z.literal("function")`
  // so the accumulator can distinguish "missing" (default to function) from
  // "present but unknown" (alert) without lying about the wire shape.
  type: z.string().optional(),
});

export type ChatCompletionChunkToolCall = z.infer<
  typeof ChatCompletionChunkToolCallSchema
>;

export const contentRolesSchema = z.union([
  z.enum(["system", "user", "assistant", "developer", "tool"]),
  z.string(),
]);

export const ChatCompletionChunkDeltaSchema = z.object({
  content: z.string().nullish(),
  reasoning_content: z.string().nullish(),
  reasoning: z.string().nullish(),
  function_call: ChatCompletionChunkFunctionCallSchema.optional(),
  // Nullable: providers send `"role": null` on non-role deltas (every chunk
  // after the first). `.optional()` rejected `null`, failing the whole chunk.
  role: contentRolesSchema.nullish(),
  // Nullable: SGLang before 0.5.22 sends `"tool_calls": null` on every delta
  // without tool calls; 0.5.22 omits the key.
  tool_calls: z.array(ChatCompletionChunkToolCallSchema).nullish(),
});

export type ChatCompletionChunkDelta = z.infer<
  typeof ChatCompletionChunkDeltaSchema
>;

export const ChatCompletionChunkChoiceSchema = z.object({
  delta: ChatCompletionChunkDeltaSchema,
  finish_reason: FinishReasonSchema.nullable(),
  index: z.number().int(),
  // Nullable to match the wire format: OpenAI (and runtimes like SGLang)
  // send `"logprobs": null` on stream chunks when logprobs are not requested.
  logprobs: LogprobsSchema.nullable().optional(),
});

export type ChatCompletionChunkChoice = z.infer<
  typeof ChatCompletionChunkChoiceSchema
>;

export const ChatCompletionChunkSchema = z.object({
  id: z.string(),
  choices: z.array(ChatCompletionChunkChoiceSchema),
  created: z.number().int(),
  model: z.string(),
  object: z.literal("chat.completion.chunk"),
  system_fingerprint: z.string().optional(),
  // Nullable to match the wire format: with `stream_options.include_usage` or
  // `continuous_usage_stats`, choice-carrying chunks report `"usage": null`
  // and only the final usage-bearing chunk is populated.
  usage: CompletionUsageSchema.nullable().optional(),
});

export type ChatCompletionChunk = z.infer<typeof ChatCompletionChunkSchema>;

export const TextCompletionChunkChoiceSchema = z.object({
  text: z.string(),
  finish_reason: FinishReasonSchema.nullable(),
  index: z.number().int(),
  // Nullable to match the wire format (see ChatCompletionChunkChoiceSchema).
  logprobs: LogprobsSchema.nullable().optional(),
  matched_stop: z.string().nullish(),
});

export type TextCompletionChunkChoice = z.infer<
  typeof TextCompletionChunkChoiceSchema
>;

export const TextCompletionChunkSchema = z.object({
  id: z.string(),
  created: z.number().int(),
  choices: z.array(TextCompletionChunkChoiceSchema),
  model: z.string(),
  object: z.literal("text_completion"),
  system_fingerprint: z.string().optional(),
  // Nullable to match the wire format (see ChatCompletionChunkSchema).
  usage: CompletionUsageSchema.nullable().optional(),
});

export type TextCompletionChunk = z.infer<typeof TextCompletionChunkSchema>;

export type CompletionChunk =
  | TextCompletionChunk
  | ChatCompletionChunk
  | ResponseStreamChunk;
